import { WHISPER_RADIUS_TILES } from '@sokdaq/protocol';

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** 타일 단위 유클리드 거리. */
export function distanceTiles(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * 반경 안에 있는지 판정한다.
 *
 * 거리 계산에 제곱근을 쓰지 않는다. 프레즌스 브로드캐스트마다
 * 인원수의 제곱만큼 호출되는 경로라 불필요한 sqrt를 피한다.
 */
export function isWithinRadius(a: Point, b: Point, radiusTiles: number): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy <= radiusTiles * radiusTiles;
}

/**
 * 맵 위의 명명된 구역. 회의실·탕비실처럼 벽으로 나뉜 공간.
 *
 * 좌표는 타일 단위이고 [x, x + width) 반열림 구간이다.
 */
export interface Zone {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface AudienceOptions {
  /** 벽으로 나뉜 구역들. 비우면 거리만으로 판정한다. */
  readonly zones?: readonly Zone[];
  readonly radiusTiles?: number;
}

/**
 * 속닥이 들리는 사람들을 고른다.
 *
 * 규칙은 소리가 어떻게 퍼지는지를 그대로 옮긴 것이다.
 * - 발화자가 구역 안에 있으면 → 그 구역 안의 사람 전부.
 *   회의실에서 한 말은 방 안 끝자리까지 들리고, 문 밖으로는 새지 않는다.
 * - 발화자가 열린 공간에 있으면 → 반경 안의 사람 중 역시 열린 공간에 있는 사람.
 *   벽 하나 사이로 두 칸 떨어져 있어도 들리지 않는다.
 *
 * 발화자 본인은 언제나 포함한다. 자기가 보낸 말은 자기 화면에 보여야 한다.
 */
export function resolveWhisperAudience(
  speakerId: string,
  positions: ReadonlyMap<string, Point>,
  options: AudienceOptions = {},
): string[] {
  const { zones = [], radiusTiles = WHISPER_RADIUS_TILES } = options;

  const origin = positions.get(speakerId);
  // 아직 좌표를 모르는 접속자(move를 한 번도 안 보냄)는 아무에게도 안 들린다.
  if (!origin) return [speakerId];

  const speakerZone = zoneAt(zones, origin);

  const audience: string[] = [];
  for (const [userId, pos] of positions) {
    if (userId === speakerId) {
      audience.push(userId);
      continue;
    }

    const listenerZone = zoneAt(zones, pos);
    if ((speakerZone?.id ?? null) !== (listenerZone?.id ?? null)) continue;

    // 같은 구역 안이면 거리를 따지지 않는다. 열린 공간이면 반경으로 자른다.
    if (speakerZone !== null || isWithinRadius(origin, pos, radiusTiles)) {
      audience.push(userId);
    }
  }
  return audience;
}

export function zoneAt(zones: readonly Zone[], point: Point): Zone | null {
  for (const zone of zones) {
    if (
      point.x >= zone.x &&
      point.x < zone.x + zone.width &&
      point.y >= zone.y &&
      point.y < zone.y + zone.height
    ) {
      return zone;
    }
  }
  return null;
}

/** 같은 구역 안에 있는 사람들. 구역 밖(zoneId=null)이면 구역 밖 사람들. */
export function resolveZoneAudience(
  zoneId: string | null,
  positions: ReadonlyMap<string, Point>,
  zones: readonly Zone[],
): string[] {
  const audience: string[] = [];
  for (const [userId, pos] of positions) {
    if ((zoneAt(zones, pos)?.id ?? null) === zoneId) {
      audience.push(userId);
    }
  }
  return audience;
}
